import { Key, useCallback, useEffect, useMemo, useState } from 'react';

import { Criterion, mergeSx, Search as DsSearch } from '@cast/design-system';

import { baseChipProps, FREE_TEXT_CHIP_KEY } from './_constants';
import { useAnalytics } from './_hooks/useAnalytics';
import { useProps } from './_hooks/useProps';
import { applyRecentSearchValue, getValuesWithTitles } from './_utils';
import { SearchProps } from './types';
import { useInitialStateReset } from '../_hooks/useInitialStateReset';
import { useRecentSearch } from '../_hooks/useRecentSearch';
import { ClientSideSearchCriterion, SearchBoxCriterion } from '../types';
import { isClient, isServer } from '../utils';

export const Search = ({
  placeholder = 'Enter search keywords',
  inputChipSx,
  ..._props
}: SearchProps) => {
  const {
    data,
    filteredData,
    criteria,
    criteriaSx,
    initialText,
    values,
    setValue,
    setValues,
    clearValue,
    disableFreeText,
    freeText,
    setFreeText,
    criteriaGuard,
    recentSearchKey,
    disableRecentSearch,
    disabled,
    searchWhileActive,
    listHeight,
    testId = 'search',
    analyticsHandler,
    size,
    chipInputProps,
    allCriteria,
    allValues,
  } = useProps(_props);

  useAnalytics(analyticsHandler, freeText, values);

  const [activeCriterionKey, setActiveCriterionKey] = useState<
    string | undefined
  >();

  const activeCriterion = useMemo(() => {
    return activeCriterionKey
      ? criteria?.find(({ key }) => key === activeCriterionKey)
      : undefined;
  }, [criteria, activeCriterionKey]);

  const [isFreeTextAsChip, setIsFreeTextAsChip] = useState(
    Boolean((freeText || initialText) && criteria?.length)
  );

  const [inputValue, setInputValue] = useState(
    isFreeTextAsChip ? '' : initialText || ''
  );

  // Sync the input with programmatic setFreeText() calls (e.g. hover-to-filter,
  // filter-chip removal, external controllers). The input is bound to the
  // local `inputValue` state, so without this an external setFreeText() updates
  // the context/URL but never the displayed value. Skipped in chip mode,
  // where free text is rendered as a chip rather than in the input. The
  // functional update avoids a feedback loop (typing sets both in lockstep, so
  // the values match and the setter bails out).
  useEffect(() => {
    if (isFreeTextAsChip) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInputValue((prev) => (prev === freeText ? prev : freeText ?? ''));
  }, [freeText, isFreeTextAsChip]);

  const searchInCriteria = useMemo(() => {
    let criteriaToShow = criteria?.filter(({ key }) => {
      // Filter available to select chips
      if (values && key in values) {
        return false;
      }
      return key !== activeCriterionKey;
    });
    if (criteriaGuard) {
      criteriaToShow = criteriaGuard({
        criteriaToShow,
        values,
        freeText: inputValue,
        selectedCriteria: activeCriterion,
      });
    }
    return criteriaToShow || [];
  }, [
    criteria,
    criteriaGuard,
    values,
    activeCriterionKey,
    inputValue,
    activeCriterion,
  ]);

  useInitialStateReset(() => {
    setActiveCriterionKey(undefined);
    setInputValue('');
    setIsFreeTextAsChip(false);
  });

  const dropdownOptions = useMemo(() => {
    if (activeCriterion?.options) {
      return activeCriterion.options;
    }
    if (!activeCriterion || (!filteredData && !data)) {
      return [];
    }
    if (isServer(activeCriterion)) {
      return [];
    }
    if (activeCriterion.getOptions) {
      return activeCriterion.getOptions(data, filteredData);
    }

    const { getValue, prop, allowEmptyValue } =
      activeCriterion as ClientSideSearchCriterion & SearchBoxCriterion;
    const _dropdownOptions = new Set<string>();
    if (allowEmptyValue) {
      _dropdownOptions.add('');
    }
    const _data = filteredData || data || [];
    for (const option of _data) {
      let value: any;
      if (getValue) {
        value = getValue(option);
      } else if (prop) {
        value = option[prop];
      }
      if (Array.isArray(value)) {
        for (const innerVal of value) {
          if (innerVal != null) {
            _dropdownOptions.add(innerVal);
          }
        }
      } else {
        if (value != null) {
          _dropdownOptions.add(value);
        }
      }
    }

    const result = Array.from(_dropdownOptions);
    if (activeCriterion.mapOptions) {
      return activeCriterion.mapOptions(result, values);
    }
    return result;
  }, [activeCriterion, data, filteredData, values]);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { recentSearches, saveRecentSearch, restoreSearch } = useRecentSearch(
    disableRecentSearch ? undefined : recentSearchKey,
    criteria || [],
    ({ freeText = '', values }) => {
      if (!disableFreeText) {
        setFreeText?.(freeText);
        if (!criteria?.length) {
          setInputValue(freeText);
        } else {
          // show free text as chip when search has criteria
          setIsFreeTextAsChip(!!freeText);
          setInputValue('');
        }
        if (activeCriterionKey) {
          setActiveCriterionKey(undefined);
        }
      }

      setValues?.(
        applyRecentSearchValue({
          recentValues: values,
          contextValues: allValues,
          allCriteria,
        })
      );
      setIsDropdownOpen(false);

      (document.activeElement as HTMLInputElement)?.blur();
    }
  );

  const [selectedOptionsState, setSelectedOptionsState] = useState<{
    key: string | undefined;
    options: Record<string, boolean>;
  }>({ key: activeCriterion?.key, options: {} });

  const selectedOptions = useMemo(
    () =>
      selectedOptionsState.key === activeCriterion?.key
        ? selectedOptionsState.options
        : {},
    [selectedOptionsState, activeCriterion?.key]
  );

  const setSelectedOptions = useCallback(
    (
      options:
        | Record<string, boolean>
        | ((prev: Record<string, boolean>) => Record<string, boolean>),
      key?: string
    ) =>
      setSelectedOptionsState((prev) => ({
        key: key ?? prev.key,
        options:
          typeof options === 'function' ? options(prev.options) : options,
      })),
    []
  );

  const selectedValuesWithTitles = useMemo(() => {
    if (criteria && values) {
      const chips = getValuesWithTitles(criteria, values, activeCriterionKey);

      if (isFreeTextAsChip) {
        chips.push({
          ...baseChipProps,
          key: FREE_TEXT_CHIP_KEY,
          testId: FREE_TEXT_CHIP_KEY,
          title: '',
          value: freeText,
        });
      }

      return chips;
    }
    return undefined;
  }, [criteria, values, activeCriterionKey, freeText, isFreeTextAsChip]);

  const activeCriterionValue = activeCriterionKey
    ? values?.[activeCriterionKey]
    : undefined;

  const clearCriteria = useCallback(
    (key?: Key) => {
      const k = key != null ? String(key) : undefined;
      if (!k) {
        setActiveCriterionKey(undefined);
        setFreeText?.(undefined);
        setIsFreeTextAsChip(false);
        setInputValue('');
        clearValue?.(Object.keys(values || {}));
      } else if (k === activeCriterionKey) {
        setActiveCriterionKey(undefined);
        setInputValue('');
        if (searchWhileActive) {
          clearValue?.(k);
        }
      } else if (k === FREE_TEXT_CHIP_KEY) {
        setFreeText?.(undefined);
        setIsFreeTextAsChip(false);
      } else {
        clearValue?.(k);
      }
    },
    [activeCriterionKey, clearValue, searchWhileActive, setFreeText, values]
  );

  const editCriteria = useCallback(
    (key?: Key) => {
      const k = key != null ? String(key) : undefined;
      if (!k) {
        return;
      }
      if (k === FREE_TEXT_CHIP_KEY) {
        setIsFreeTextAsChip(false);
        setInputValue(freeText ?? '');
      } else {
        const criterion = criteria?.find((c) => c.key === k);

        if (!criterion) {
          return;
        }

        setActiveCriterionKey(criterion.key);
        /**
         * Clear previously selected values when single selection is allowed
         * or multiple selections allowed but no options are given
         */
        if (
          !criterion.allowMultiple ||
          (criterion.allowMultiple && !criterion.options?.length)
        ) {
          const prev = values?.[k];
          setSelectedOptions(
            prev && Array.isArray(prev)
              ? prev.reduce((acc: Record<string, boolean>, curr: string) => {
                  return { ...acc, [curr]: true };
                }, {})
              : {},
            criterion.key
          );
        } else {
          const criterionValues = values?.[criterion.key];
          if (Array.isArray(criterionValues)) {
            setSelectedOptions(
              criterionValues.reduce((acc, curr) => {
                acc[curr] = true;
                return acc;
              }, {}),
              criterion.key
            );
          }
        }
      }
    },
    [criteria, freeText, setSelectedOptions, values]
  );
  const onApply = useCallback(() => {
    if (!activeCriterionKey) {
      return;
    }
    if (activeCriterion?.allowMultiple) {
      const options = Object.entries(selectedOptions)
        .filter(([_, checked]) => checked)
        .map(([option]) => option);
      const checkedOptions = options.length ? options : undefined;
      setValue?.(
        activeCriterionKey,
        activeCriterion?.mapOptionOnSelect
          ? activeCriterion.mapOptionOnSelect(checkedOptions)
          : checkedOptions
      );
    }
    setInputValue('');
    setActiveCriterionKey(undefined);
  }, [activeCriterion, activeCriterionKey, selectedOptions, setValue]);

  return (
    <DsSearch
      rootSx={mergeSx(
        {
          width: '100%',
        },
        _props.rootSx
      )}
      disabled={disabled}
      inputChipSx={inputChipSx}
      searchWhileActive={searchWhileActive}
      disableFreeText={disableFreeText || isFreeTextAsChip}
      activeCriterion={activeCriterion}
      activeCriterionValue={activeCriterionValue}
      selectedOptions={selectedOptions}
      toggleOption={(optionKey: string) => {
        setSelectedOptions((currentValue) => ({
          ...currentValue,
          [optionKey]: !currentValue[optionKey],
        }));
      }}
      criteriaSearchProps={{
        criteria: searchInCriteria,
        onSelect: (criterion: Criterion) => {
          setActiveCriterionKey(criterion.key);
          setSelectedOptions({}, criterion.key);
          setInputValue('');
          if (freeText) {
            setIsFreeTextAsChip(true);
          }
          if (searchInCriteria.length || activeCriterionKey) {
            setIsDropdownOpen(true);
          }
        },
        criteriaSx,
      }}
      recentSearchProps={{
        recentSearches,
        criteria,
        onSelect: restoreSearch,
      }}
      chipInputProps={{
        activeTitle: activeCriterion
          ? activeCriterion.title || activeCriterion.key
          : undefined,
        inputProps: {
          placeholder:
            !activeCriterion && !selectedValuesWithTitles?.length
              ? placeholder
              : undefined,
        },
        chips: selectedValuesWithTitles,
        onClear: () => {
          clearCriteria();
        },
        onKeyDown: (e) => {
          // Only handle Enter on server side search without predefined options
          if (
            !activeCriterionKey ||
            (activeCriterion && isClient(activeCriterion)) ||
            activeCriterion?.options
          ) {
            return;
          }
          if (e.key === 'Enter') {
            setValue?.(activeCriterionKey, inputValue);
            setActiveCriterionKey(undefined);
            setInputValue('');
          }
        },
        disabled,
        size,
        ...chipInputProps,
      }}
      dropdownProps={{
        options: dropdownOptions,
        open: isDropdownOpen,
        onOpen: () => {
          if (searchInCriteria.length || activeCriterionKey) {
            setIsDropdownOpen(true);
          }
        },
        onClose: () => {
          setIsDropdownOpen(false);
        },
        onChange: (e, v) => {
          if (!activeCriterionKey) {
            return;
          }
          if (!activeCriterion?.allowMultiple) {
            setValue?.(
              activeCriterionKey,
              activeCriterion?.mapOptionOnSelect
                ? activeCriterion.mapOptionOnSelect(v as string[])
                : v
            );
          }
          setInputValue('');
          setActiveCriterionKey(undefined);
        },
        onInputChange: (value: string, reason: 'reset' | 'change') => {
          if (reason !== 'reset') {
            setInputValue(value);
            if (!activeCriterion) {
              setFreeText?.(value);
            } else if (searchWhileActive && activeCriterionKey) {
              setValue?.(activeCriterionKey, value);
            }
          }
        },
      }}
      autocompleteProps={{
        inputValue,
      }}
      clearCriteria={clearCriteria}
      editCriteria={editCriteria}
      onOptionsClose={onApply}
      listHeight={listHeight}
      testId={testId}
      onPopperClose={() => {
        if (freeText && !isFreeTextAsChip && criteria?.length) {
          setIsFreeTextAsChip(true);
          setInputValue('');
        }
        saveRecentSearch({ freeText, values });
      }}
    />
  );
};
